import { makeToast } from "@/shared/utils/toast";
import { config } from "@/shared/utils/config";
import { useI18n } from "vue-i18n";
import { onMounted, onUnmounted, ref, Ref } from "vue";
import useLogging from "@/shared/composables/useLogging";

export default function useCaptcha(
  submitCallback: (captchaValue?: string) => void,
  vueRecaptcha: Ref
) {
  const t = useI18n().t;
  const vueRecaptchaLoading = ref(false);
  const captchaValue = ref("");
  const recaptchaSiteKey = config.recaptchaSiteKey;
  const isCaptchaEnabled = config.isCaptchaEnabled;
  /** Will be set to true on `window.onload` */
  const hasRecaptchaLoaded = ref(false);
  const recaptchaRetryCount = ref(0);
  const { submitInfoToApi, submitErrorToApi } = useLogging();

  const captchaHandler = async () => {
    /**
     * If captcha is enabled, execute recaptcha first then submit the form
     * Form submission is handled by recaptchaVerified
     */
    if (isCaptchaEnabled) {
      vueRecaptchaLoading.value = true;
      submitInfoToApi("recaptcha: captchaHandler");

      try {
        // throw "recaptcha: Mock error";
        vueRecaptcha.value.reset();
        vueRecaptcha.value.execute();
      } catch (e) {
        // Only enters when there's an error
        // Retry captchaHandler with max retries
        if (recaptchaRetryCount.value < config.captchaMaxRetryCount) {
          // Add retry count
          recaptchaRetryCount.value++;

          // Submit Error
          submitErrorToApi("recaptcha Error: ", { e });

          submitInfoToApi("recaptcha: RETRY captchaHandler: ", {
            recaptchaRetryCount: recaptchaRetryCount.value,
          });

          // Delay, then immediately return a retry call
          await new Promise((resolve) => setTimeout(resolve, 500)); // delay for 250ms to prevent blocking
          return captchaHandler();
        }
      }

      // Always reset retry count, and reset loading
      // Only enters when retry count is > 0, which entered the error on try-catch
      if (recaptchaRetryCount.value > 0) {
        recaptchaRetryCount.value = 0;
        vueRecaptchaLoading.value = false;
        submitInfoToApi("recaptcha: Reset RETRY: ", {
          recaptchaRetryCount: recaptchaRetryCount.value,
        });
      }

      // if the vuerecaptcha is still loading
      setTimeout(() => {
        // if the captcha is still loading
        if (vueRecaptchaLoading.value) {
          // reset captcha
          recaptchaRetryCount.value = 0;
          // reset captcha loading
          vueRecaptchaLoading.value = false;

          // return to parent component
          submitCallback();
        }
      }, 5000);
    } else {
      submitCallback();
    }
  };

  const recaptchaVerified = (response) => {
    /**
     * On recaptchaVerified, send captcha response
     */
    vueRecaptchaLoading.value = false;
    {
      submitInfoToApi("response", { response });
      submitCallback(response);
    }
  };
  const recaptchaError = () => {
    vueRecaptchaLoading.value = false;
    makeToast(
      "error",
      t("Error"),
      t(
        "An error occurred during Captcha verification. Please ensure that the page has totally loaded before submitting. Otherwise, please refresh the page and try again."
      )
    );
  };

  /** Wait for window onload before setting loading to false */
  const onLoadCallback = () => {
    if (!isCaptchaEnabled) return;

    // eslint-disable-next-line
    const _w = window as any;

    if (_w.grecaptcha) {
      submitInfoToApi("grecaptcha?", { grecaptcha: _w.grecaptcha });
      hasRecaptchaLoaded.value = true;
    }

    // Fallback set to `true`
    setTimeout(() => {
      hasRecaptchaLoaded.value = true;
    }, 1000);
  };

  onMounted(() => {
    // Ensures that when this component is remounted after onload, that loading would still be set to false.
    onLoadCallback();
    window.addEventListener("load", onLoadCallback);
  });

  onUnmounted(() => {
    // Cleanup event listeners
    window.removeEventListener("load", onLoadCallback);
  });

  return {
    isCaptchaEnabled,
    recaptchaError,
    captchaHandler,
    recaptchaVerified,
    vueRecaptcha,
    vueRecaptchaLoading,
    t,
    captchaValue,
    submitCallback,
    recaptchaSiteKey,
  };
}
